import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  PartnershipInformationListRequest,
  useDeletePartnershipInformationMutation,
  useFetchPartnershipInformationsQuery,
} from '../partnershipinformation-api';

import AuditLogsScreen from '../../../components/common/auditLogs';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
  Tooltip,
  Select,
  Rate,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleFilled,
  ExclamationCircleOutlined,
  HomeOutlined,
  MoreOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { PartnershipInformationViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize, DefaultPollingInterval } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function PartnershipInformationsScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const sortBy = query.get('sortBy') || '';
    if (!sortBy) return null;
    
    // Map frontend column key to backend column name
    const columnMapping: { [key: string]: string } = {
      'Id': 'Id',
      'name': 'Name',
      'type': 'Type',
      'partnerId': 'PartnerId',
      'identifierType': 'IdentifierType',
      'identifierValue': 'IdentifierValue',
      'partner_Van': 'Partner_VAN',
      'partnerRating': 'PartnerRating',
    };
    
    const backendColumnName = columnMapping[columnKey] || columnKey;
    
    // Split sortBy to get column name and direction (e.g., "IdentifierType asc" -> ["IdentifierType", "asc"])
    const sortByParts = sortBy.trim().split(/\s+/);
    const sortColumnName = sortByParts[0];
    const sortDirection = sortByParts[1]?.toLowerCase() || '';
    
    // Check if the column name matches exactly (case-insensitive)
    if (sortColumnName.toLowerCase() === backendColumnName.toLowerCase()) {
      return sortDirection === 'desc' ? 'descend' : 'ascend';
    }
    return null;
  };
  
  const partnershipInformationTable: TableState = {
    columns: [
      {
        title: 'Id',
        dataIndex: '',
        key: 'Id',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
              <Space>
                <span>{partnershipInformation.id}</span>
              </Space>
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('Id'),
        fixed: 'left',
      },
      {
        title: 'Name',
        dataIndex: '',
        key: 'name',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
              <span>{partnershipInformation.name}</span>
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('name'),
      },
      {
        title: 'Type',
        dataIndex: '',
        key: 'type',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
              <span>{partnershipInformation.type}</span>
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('type'),
      },
      {
        title: 'Partner Id',
        dataIndex: '',
        key: 'partnerId',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
              <span>{partnershipInformation.partnerId}</span>
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('partnerId'),
      }, 
      {
        title: 'IdentifierType',
        dataIndex: '',
        key: 'identifierType',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
              <span>{partnershipInformation.identifierType}</span>
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('identifierType'),
      },
      {
        title: 'IdentifierValue',
        dataIndex: '',
        key: 'identifierValue',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
              <span>{partnershipInformation.identifierValue}</span>
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('identifierValue'),
      },
      {
        title: 'Partner/VAN',
        dataIndex: '',
        key: 'partner_Van',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
              <span>{partnershipInformation.partner_VAN}</span>
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('partner_Van'),
      },      
      
      {
        title: 'Notes',
        dataIndex: '',
        key: 'notes',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
          <Tooltip title={partnershipInformation.notes}>
          <span>{partnershipInformation.notes}</span>
         </Tooltip>
              
          </>
        )
      }, 
      {
        title: 'Partner Rating',
        dataIndex: '',
        key: 'partnerRating',
        fixed:'right',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <>
          <Rate disabled={true} allowHalf value={partnershipInformation.partnerRating} />
          </>
        ),
        sorter: true,  // Enable sorter to show sort indicator
        sortOrder: getSortOrderForColumn('partnerRating'),
      },      
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (partnershipInformation: PartnershipInformationViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<InfoCircleFilled />} key="2">
                  <Link
                    to={getLink('CarrierOnboardingList', {
                      tenantId: params.tenantId,
                      id: partnershipInformation.id,
                      partnerId: partnershipInformation.partnerId,
                      identifierValue:partnershipInformation.identifierValue,
                      carrierName:partnershipInformation.name,
                    })}
                  >
                    View Partner Onboarding
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('PartnershipInformationEdit', {
                      tenantId: params.tenantId,
                      Id: partnershipInformation.id,
                    })}
                  >
                    View
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="4">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key="3"
                  disabled={!!(deletingId && deletingId === partnershipInformation.id)}
                  onClick={() => {
                    confirm({
                      title: (
                        <span>
                          Delete partnership Information <b>{partnershipInformation.name}</b>?
                        </span>
                      ),
                      content:
                        'This will also delete its contacts, ratings, partner maps, and any ' +
                        'splitting rules used only by this partner along with their routing rules, ' +
                        'partner EDI X12 records and connection endpoints. This cannot be undone.',
                      icon: <ExclamationCircleOutlined />,
                      okText: 'Yes',
                      cancelText: 'No',
                      onOk: () => handleDelete(partnershipInformation),
                    });
                  }}
                >
                  {deletingId === partnershipInformation.id ? 'Deleting...' : 'Delete'}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): PartnershipInformationListRequest => {
    const urlParams = new URLSearchParams(location.search);
    return {
      current: Number(urlParams.get('p')) || DefaultPage,
      pageSize: Number(urlParams.get('size')) || DefaultPageSize,
      filter: urlParams.get('q'),
      sortBy: urlParams.get('sortBy') || 'EnteredByDate Desc',
      searchText: urlParams.get('searchText') || '',
      type: urlParams.get('type') || '',
    };
  };

  const [pagination, setPagination] = useState<PartnershipInformationListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<PartnershipInformationViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchPartnershipInformationsQuery(pagination);
  
  // Update pagination when URL query params change (including page number)
  useEffect(() => {
    const newState = getCurrentState();
    setPagination(newState);
  }, [location.search]);

  const [deletePartnershipInformation, { isLoading: isDeleteLoading, error: deleteError }] =
    useDeletePartnershipInformationMutation();
  const { confirm } = Modal;

  const onCreateNew = useCallback(
    () => navigate(getLink('PartnershipInformationEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );
  const handleCarrierChange = (value: string) => {
      value = value===undefined?'':value;
      localStorage.setItem('type',value||'');
      query.set('type', value);
      let searchText =query.get('searchText')||'';
      navigate(
        getLink(
          'PartnershipInformationList',
          { ...params },
          {
            ...query,
            sortBy: query.get('sortBy')||'EnteredByDate Desc',
            p: DefaultPage,
            size: DefaultPageSize,            
            searchText:query.get('searchText')||'',
            type:query.get('type')||'',
            q:encodeURIComponent(searchText?.length>0 ? `(Name:*${searchText.replaceAll(" ", "\\ ")}* OR identifierValue:*${searchText.replaceAll(" ", "\\ ")}* OR partnerId:*${searchText.replaceAll(" ", "\\ ")}*) ${value.length>0?'AND Type:'+value:''}` : value?.length >0 ?`Type:"${value}"`:''),
          }
        )
      );

  };
  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<PartnershipInformationViewModel> | SorterResult<PartnershipInformationViewModel>[],
    extra: TableCurrentDataSource<PartnershipInformationViewModel>
  ) => {
    // Build new sort parameter
    const defaultSort = 'EnteredByDate Desc';
    // Read current sort from URL to ensure fresh value
    const urlParams = new URLSearchParams(location.search);
    const currentSortBy = urlParams.get('sortBy') || defaultSort;
    const currentPage = Number(urlParams.get('p')) || 1;
    
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? (sorter.length > 0 ? sorter[0] : null) : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<PartnershipInformationViewModel>;
    
    // Determine new page number - ensure it's always a valid number
    // When clicking pagination, paginationConfig.current should always be set
    let newPage = paginationConfig.current;
    
    // If paginationConfig.current is invalid, it might mean:
    // 1. We're changing page size (paginationConfig.pageSize is set but current is not)
    // 2. We're just sorting (columnKey is set)
    // 3. Something went wrong - in this case, keep current page
    if (newPage === undefined || newPage === null || isNaN(newPage) || newPage < 1) {
      if (columnKey) {
        // Changing sort - reset to page 1
        newPage = 1;
      } else if (paginationConfig.pageSize && paginationConfig.pageSize !== Number(urlParams.get('size'))) {
        // Changing page size - reset to page 1
        newPage = 1;
      } else {
        // No valid page number - this shouldn't happen on pagination clicks
        // But if it does, keep current page to avoid breaking navigation
        newPage = currentPage;
      }
    }
    
    // Ensure newPage is always a positive integer
    newPage = Math.max(1, Math.floor(newPage));
    
    // Determine new sort - always ensure it's a valid string
    let newSortBy: string = defaultSort;
    
    // Check if we're actually changing sort or just pagination
    // When clicking pagination (not sorting), sorter might be null/undefined
    // When sort is already cancelled, sorter might have columnKey but order is null
    const isSortingAction = columnKey && order; // User clicked a column header with sort direction
    const isCancellingSort = columnKey && !order && currentSortBy !== defaultSort; // User cancelled an active sort
    const isPaginationOnly = !columnKey || (!order && currentSortBy === defaultSort); // Just pagination, sort already default

    // Handle sorting: Ant Design cycles through: null -> ascend -> descend -> null
    if (isSortingAction) {
      // Order is present (ascend or descend) - user is actively sorting
      // Map frontend column key to backend column name
      const columnMapping: { [key: string]: string } = {
        'Id': 'Id',
        'name': 'Name',
        'type': 'Type',
        'partnerId': 'PartnerId',
        'identifierType': 'IdentifierType',
        'identifierValue': 'IdentifierValue',
        'partner_Van': 'Partner_VAN',
        'partnerRating': 'PartnerRating',
      };
      
      const clickedColumn = columnKey.toString();
      const backendColumnName = columnMapping[clickedColumn] || clickedColumn;
      newSortBy = getSortBy(backendColumnName, order);
      
      // Reset to page 1 on sort change
      if (newSortBy !== currentSortBy) {
        newPage = 1;
      }
    } else if (isCancellingSort) {
      // Order is null/undefined AND we had an active sort - user is cancelling sort
      newSortBy = defaultSort;
      newPage = 1;
    } else {
      // Pagination only - preserve current sort and use the page number from paginationConfig
      // Don't reset page to 1 if we're just changing pages
      newSortBy = currentSortBy || defaultSort;
      // newPage is already set from paginationConfig.current above
    }
    
    // Final safety check - ensure newSortBy is never empty
    if (!newSortBy || newSortBy.trim() === '') {
      newSortBy = defaultSort;
    }

    // Ensure pageSize is valid
    const newPageSize = paginationConfig.pageSize || Number(urlParams.get('size')) || 20;
    
    // Build query parameters object
    const queryParams: any = {
      sortBy: newSortBy,
      p: newPage,
      size: newPageSize,
    };
    
    // Add optional parameters only if they have values
    const qValue = urlParams.get('q');
    if (qValue) {
      queryParams.q = qValue;
    }
    
    const searchTextValue = urlParams.get('searchText');
    if (searchTextValue) {
      queryParams.searchText = searchTextValue;
    }
    
    const typeValue = urlParams.get('type');
    if (typeValue) {
      queryParams.type = typeValue;
    }
    
    const newUrl = getLink(
      'PartnershipInformationList',
      { ...params },
      queryParams
    );
    
    // Always navigate - React Router should detect the change
    // The key is ensuring newPage is different from currentPage when clicking pagination
    navigate(newUrl);
  };

  const handleSearch = (searchText: string) => {
    let type = query.get('type') || '';
    navigate(
      getLink(
        'PartnershipInformationList',
        { ...params },
        {
          p: DefaultPage,
          size: pagination.pageSize,
          searchText: searchText,
          type: type,
          q: encodeURIComponent(searchText?.length > 0 ? `(Name:*${searchText.replaceAll(" ", "\\ ")}* OR identifierValue:*${searchText.replaceAll(" ", "\\ ")}* OR partnerId:*${searchText.replaceAll(" ", "\\ ")}*) ${type.length > 0 ? 'AND Type:' + type : ''}` : type?.length > 0 ? `Type:"${type}"` : ''),
          sortBy: query.get('sortBy') || '' || '',  // Preserve current sort
        }
      )
    );
  };

  // antd's message API renders its argument as a React node, so anything that is
  // not a string (e.g. a ProblemDetails body) would throw and trip the global
  // ErrorBoundary instead of showing a toast.
  const toDisplayMessage = (value: any, fallback: string): string => {
    if (typeof value === 'string' && value.trim()) return value;
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') {
      const candidate = value.message ?? value.detail ?? value.title ?? value.error;
      if (typeof candidate === 'string' && candidate.trim()) return candidate;
      return fallback;
    }
    return String(value);
  };

  const handleDelete = async (partnershipInformation: PartnershipInformationViewModel) => {
    const idToDelete = partnershipInformation.id ?? 0;
    try {
      setDeletingId(idToDelete);
      const response = await deletePartnershipInformation({
        partnershipinformation: {
          Id: idToDelete,
          updatedBy: sessionStorage.getItem('userEmail') || '',
        },
      }).unwrap();

      if (response?.status?.toLowerCase() === 'success') {
        message.success(
          toDisplayMessage(response?.message, `Partner Information ${partnershipInformation.name} deleted`)
        );
      } else {
        message.error(toDisplayMessage(response?.message, 'Failed to delete partner information'));
      }
    } catch (error: any) {
      console.error('Delete partner information failed', error);
      message.error(toDisplayMessage(error?.data ?? error, 'Failed to delete partner information'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        extra={
          <Space>
          <Button htmlType="button" data-testid={'create-new-partnershipInformation'} onClick={onCreateNew}>
            New Partner Information
          </Button>
          
          </Space>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Partner Information
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="partnershipInformation">
              <Link to={getLink('PartnershipInformationList', { tenantId: params.tenantId })}>Partner Information</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert
              message="Error"
              description={toDisplayMessage(
                (deleteError as any)?.data ?? deleteError,
                'Failed to delete partner information'
              )}
              type="error"
              showIcon
            />
          )}
          <Space direction="vertical" style={{width: '100%'}}  size="large">
            <Space size="large" direction="horizontal">
            <div style={{minWidth: 200}}>
              <b>Type: </b>
            </div>
              <Select defaultValue={query.get('type')||''}  onChange={handleCarrierChange} allowClear placeholder='Type' style={{minWidth: 150}} options={[{label:'Carrier', value:'Carrier'},{label:'Customer', value:'Customer'}]} />
              <div style={{minWidth: 150}}>
              <b>Search: </b>
              </div>
              <Input.Search
              placeholder="Search by Name or IdentifierValue or Partner Id"
              onSearch={handleSearch}
              enterButton
              style={{ width: 400 }}
              loading={isFetching}
              allowClear
              defaultValue={query.get('searchText')||''}
            />
            </Space>
            
            <Table scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              onRow={(record:any, rowIndex) => {
                return {
                  onClick: event => {
                    setEntityId(record?.id||0);
                    localStorage.setItem('partnershipId', record?.id??0);
                    // sessionStorage.setItem('overallRating', record?.partnerRating);
                    sessionStorage.setItem('carrierName', record?.name.trimStart()||'');
                    localStorage.setItem('partnerInformationId', record?.id??0);
                    sessionStorage.setItem('partnerId',record?.partnerId);
                    sessionStorage.setItem('identifierValue',record?.identifierValue);
                  },
                };
              }}
              columns={partnershipInformationTable.columns}
              loading={isFetching}
            />
          </Space>
        </Card>
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'PartnerInformation'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </div>
  );
}
